/**
 * TitleBlock component for the Indigo design system.
 *
 * A heading paired with trailing controls — most often a `RadioGroup` in its
 * `selector` variant, i.e. the "question + answer" row used by onboarding and
 * preference forms. An optional leading index renders the row as a numbered
 * question, and an optional description sits under the title.
 *
 * It is the heading-weight counterpart to `LabelBlock`: `LabelBlock` puts a
 * small tertiary label above a value, `TitleBlock` puts a full heading beside
 * a control.
 *
 * @example
 * ```tsx
 * <TitleBlock
 *   index={5}
 *   title="Contact for flex time"
 *   description="Communicate to physician…"
 *   controls={
 *     <RadioGroup
 *       variant="selector"
 *       value={answer}
 *       onChange={setAnswer}
 *       options={[
 *         { value: "ask", label: "Ask me first" },
 *         { value: "no-ask", label: "No need to ask" },
 *       ]}
 *     />
 *   }
 * />
 * ```
 */
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { CSSProperties, forwardRef, ReactNode } from "react";
import { Icon } from "../Icons";
import { Tooltip } from "../Tooltip";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./TitleBlock.css";

/**
 * Size → heading level. The default `md` is H2 (16/20 bold), the size the
 * designer specced for question rows.
 */
const HEADING_LEVEL_BY_SIZE = {
  sm: 3,
  md: 2,
  lg: 1,
} as const;

/**
 * Typography paints its own default color through
 * `.indigo-typography-base:not([class*="text-"])`, which outranks a plain
 * component class — so the tone has to arrive as a Tailwind `text-` utility,
 * the escape hatch that rule leaves open (same as `OutlinedCard`).
 */
const titleVariants = cva("indigo-title-block__title", {
  variants: {
    tone: {
      primary: "text-indigo-text-primary",
      secondary: "text-indigo-text-secondary",
    },
  },
  defaultVariants: {
    tone: "secondary",
  },
});

export interface TitleBlockProps {
  /**
   * Heading text (or nodes, so the title can carry inline content).
   */
  title: ReactNode;

  /**
   * Optional leading number, rendered as `5.` before the title. Use it when
   * the block is one question in an ordered list.
   */
  index?: number;

  /**
   * Optional description rendered under the title.
   */
  description?: ReactNode;

  /**
   * Controls rendered at the trailing (right) edge of the title row — e.g. a
   * `RadioGroup variant="selector"`, a `Switch`, or a button.
   */
  controls?: ReactNode;

  /**
   * Fixed width of the controls column (CSS length or px number). Give stacked
   * blocks the same value and their controls line up in a column, no matter
   * how long each title is.
   */
  controlsWidth?: number | string;

  /**
   * Heading size: `sm` = H3, `md` = H2, `lg` = H1.
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * Title color. `secondary` is the default for question rows; `primary` is
   * for a block that heads a whole section.
   * @default "secondary"
   */
  tone?: "primary" | "secondary";

  /**
   * Vertical alignment of the controls against the title.
   * @default "center"
   */
  align?: "start" | "center";

  /**
   * Tooltip text shown on a "?" icon next to the title.
   */
  infoTooltip?: string;

  /**
   * Dims the block and stops pointer interaction with the controls. It is a
   * presentational state only — also pass `disabled` to the control itself so
   * keyboard users can't reach it either.
   * @default false
   */
  disabled?: boolean;

  /**
   * Content rendered below the title row and description — e.g. a note field
   * belonging to the question.
   */
  children?: ReactNode;

  /**
   * Additional CSS classes applied to the block container.
   */
  className?: string;

  /**
   * Additional CSS classes applied to the `children` wrapper.
   */
  contentClassName?: string;
}

export const TitleBlock = forwardRef<HTMLDivElement, TitleBlockProps>(
  (
    {
      title,
      index,
      description,
      controls,
      controlsWidth,
      size = "md",
      tone = "secondary",
      align = "center",
      infoTooltip,
      disabled = false,
      children,
      className,
      contentClassName,
    },
    ref,
  ) => {
    // The width is caller-supplied and arbitrary, so it can only be set
    // inline — but it goes through a custom property rather than `width`, so
    // the narrow-viewport rule in the stylesheet can still override it.
    const controlsStyle = (
      controlsWidth === undefined
        ? undefined
        : {
            "--indigo-title-block-controls-width":
              typeof controlsWidth === "number"
                ? `${controlsWidth}px`
                : controlsWidth,
          }
    ) as CSSProperties | undefined;

    return (
      <div
        ref={ref}
        className={clsx(
          "indigo-title-block",
          disabled && "indigo-title-block--disabled",
          className,
        )}
        aria-disabled={disabled || undefined}
      >
        <div
          className={clsx(
            "indigo-title-block__row",
            `indigo-title-block__row--${align}`,
          )}
        >
          <div className="indigo-title-block__heading">
            {/* The index lives inside the heading rather than beside it, so the
                row stays a single heading element for screen readers. */}
            <Heading
              level={HEADING_LEVEL_BY_SIZE[size]}
              className={titleVariants({ tone })}
            >
              {index !== undefined && (
                <span className="indigo-title-block__index">{index}.</span>
              )}
              {title}
            </Heading>
            {infoTooltip && (
              <Tooltip content={infoTooltip} placement="top-start" delay={200}>
                <span className="indigo-title-block__info">
                  <Icon name="functional-question" size="s" />
                </span>
              </Tooltip>
            )}
          </div>

          {controls && (
            <div className="indigo-title-block__controls" style={controlsStyle}>
              {controls}
            </div>
          )}
        </div>

        {description && (
          <Body
            size="sm"
            className="indigo-title-block__description text-indigo-text-tertiary"
          >
            {description}
          </Body>
        )}

        {children && (
          <div
            className={clsx("indigo-title-block__content", contentClassName)}
          >
            {children}
          </div>
        )}
      </div>
    );
  },
);

TitleBlock.displayName = "TitleBlock";

export default TitleBlock;
